import { CustomSpaceTypeModalCard } from 'spw-components/dialogs/custom-space-types/styles';
import { Box, Dialog, DialogContent } from '@mui/material';
import { isEmpty, isNil } from 'ramda';
import { ErrorMessage, FormControlLabel } from 'spw-components/dialogs/styles';
import {
  SPWBaseCheckbox,
  SPWCancelButton88Width,
  SPWDialogActions,
  SPWSaveButton88Width,
} from 'spw-shared/globalComponents';
import WarningIconRedSmall from 'spw-static/images/warning-icon-red-small.svg';
import { colors } from 'spw-theme';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  IProjectSpaceType,
  ISpaceType,
} from 'spw-redux/modules/space_types/types';
import { useEnterShortcut } from 'spw-esa-projects/building-details/floorplan/hooks';
import { useDispatch, useSelector } from 'react-redux';
import { selectProjectSpaceTypes } from 'spw-redux/modules/space_types/selectors';
import {
  selectAllChildWorksheetSpaces,
  selectWorksheet,
} from 'spw-redux/modules/calculators/selectors';
import { actions as customSpaceTypeActions } from 'spw-redux/modules/custom_spaces/actions';
import { actions as calculatorActions } from 'spw-redux/modules/calculators/actions';
import { useSpaceTypeDialogTitle } from 'spw-components/dialogs/custom-space-types/hooks';
import InfoBlackSmall from 'spw-static/images/info-black-small.svg';
import {
  selectSelectedChildSpaceType,
  selectSelectedSpaceType,
  selectSelectedSpaceTypeCategory,
} from 'spw-redux/modules/worksheet/selectors';
import {
  getCategoryOfSpaceType,
  getDefaultCategory,
  getPreviousId,
} from 'spw-programming-projects/building-details/worksheet/utils';
import {
  allCategories,
  selectProjectSpaceTypeCategories,
} from 'spw-redux/modules/projects/selectors';
import { updateProjectSpaceType } from 'spw-redux/modules/space_types/actions';
import { isDuplicateSpace } from 'spw-components/worksheet/utils';
import { WorksheetInput } from 'spw-components/worksheet/styles';
import { textAreaReachedMaxRows } from 'spw-programming-projects/building-details/worksheet/parent-space-type-row';
import { CUSTOM_SPACE_TYPE_NAME_INPUT_WIDTH } from 'spw-programming-projects/constants';

type Props = {
  isOpen: boolean;
  onDismiss: (name?: string, clientFacingName?: string) => void;
  parentSpaceType?: ISpaceType;
  isEditingSpaceType?: boolean;
  onCreate?: (parentSpaceType: IProjectSpaceType) => void;
};

const InputLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  font-family: Roboto;
`;

const WorksheetSpaceTypeDialog = ({
  isOpen,
  onDismiss,
  parentSpaceType,
  isEditingSpaceType,
  onCreate,
}: Props) => {
  const dispatch = useDispatch();
  const defaultCategories = useSelector(allCategories);
  const projectSpaceTypes = useSelector(selectProjectSpaceTypes);
  const projectSpaceType = useSelector(selectSelectedSpaceType);
  const childSpaceType = useSelector(selectSelectedChildSpaceType);
  const spaceTypeCategories = useSelector(selectProjectSpaceTypeCategories);
  const worksheet = useSelector(selectWorksheet);
  const selectedSpaceTypeCategory = useSelector(
    selectSelectedSpaceTypeCategory
  );
  const childWorksheetSpaceTypes = useSelector(selectAllChildWorksheetSpaces);

  const [customSpaceTypeName, setCustomSpaceTypeName] = useState(() => {
    if (!isEditingSpaceType) return '';

    if (childSpaceType) {
      return childSpaceType?.name;
    }
    return projectSpaceType?.spaceType?.name ?? '';
  });
  const [initialCustomSpaceTypeName] = useState(() => {
    if (!isEditingSpaceType) return '';

    if (childSpaceType) {
      return childSpaceType?.name;
    }
    return projectSpaceType?.spaceType?.name ?? '';
  });

  const [clientFacingName, setClientFacingName] = useState(() => {
    if (!isEditingSpaceType) return '';

    if (childSpaceType) {
      return childSpaceType?.clientFacingName
        ? childSpaceType.clientFacingName
        : customSpaceTypeName;
    }

    return projectSpaceType?.clientFacingName
      ? projectSpaceType.clientFacingName
      : customSpaceTypeName;
  });
  const [initialClientFacingName] = useState(() => {
    if (!isEditingSpaceType) return '';

    if (childSpaceType) {
      return childSpaceType?.clientFacingName
        ? childSpaceType.clientFacingName
        : customSpaceTypeName;
    }

    return projectSpaceType?.clientFacingName
      ? projectSpaceType.clientFacingName
      : customSpaceTypeName;
  });

  const [isFormDirty, setIsFormDirty] = useState(false);

  const internalNameAlreadyInUse = useMemo(() => {
    return isDuplicateSpace(
      projectSpaceTypes,
      childWorksheetSpaceTypes,
      customSpaceTypeName.toLowerCase().trim(),
      childSpaceType?.id || projectSpaceType?.spaceType?.id
    );
  }, [
    projectSpaceTypes,
    childWorksheetSpaceTypes,
    customSpaceTypeName,
    childSpaceType,
    projectSpaceType,
  ]);

  const namesAreTheSame = useMemo(() => {
    if (!customSpaceTypeName && !clientFacingName) return true;

    const cleanName = customSpaceTypeName.trim();
    const cleanClientFacingName = clientFacingName.trim();

    return cleanName === cleanClientFacingName;
  }, [clientFacingName, customSpaceTypeName]);

  const clientNameAlreadyInUse = useMemo(() => {
    return isDuplicateSpace(
      projectSpaceTypes,
      childWorksheetSpaceTypes,
      clientFacingName.toLowerCase().trim(),
      childSpaceType?.id || projectSpaceType?.spaceType?.id
    );
  }, [
    projectSpaceTypes,
    childWorksheetSpaceTypes,
    clientFacingName,
    childSpaceType,
    projectSpaceType,
  ]);

  const saveButtonIsEnabled = useMemo(() => {
    if (internalNameAlreadyInUse || clientNameAlreadyInUse) return false;

    const cleanName = customSpaceTypeName?.trim();
    const cleanClientFacingName = clientFacingName?.trim();

    return (
      !(isEmpty(cleanName) || isEmpty(cleanClientFacingName)) && isFormDirty
    );
  }, [
    clientFacingName,
    customSpaceTypeName,
    internalNameAlreadyInUse,
    clientNameAlreadyInUse,
    isFormDirty,
  ]);

  const handleSave = () => {
    if (!saveButtonIsEnabled) return;

    const cleanName = customSpaceTypeName.trim();
    const cleanClientFacingName = clientFacingName.trim();

    // Is creating a new custom space type
    if (!isEditingSpaceType) {
      // Is creating a new custom space type child
      if (projectSpaceType) {
        dispatch(
          customSpaceTypeActions.createCustomSpaceTypeChild({
            spaceTypeId: projectSpaceType.spaceType.id,
            name: cleanName,
            clientFacingName: cleanClientFacingName,
            unitsHigh: null,
            callback: () => {
              if (onCreate) {
                onCreate(projectSpaceType);
              }
            },
          })
        );
      } else {
        dispatch(customSpaceTypeActions.setCurrentParent(parentSpaceType));
        const spaceTypeCategory =
          selectedSpaceTypeCategory ||
          getCategoryOfSpaceType(
            projectSpaceType ?? childSpaceType,
            spaceTypeCategories
          );

        const previousId = getPreviousId(worksheet.rows, spaceTypeCategory);
        const defaultCategory = getDefaultCategory(
          spaceTypeCategory,
          defaultCategories
        );

        dispatch(
          customSpaceTypeActions.createCustomSpaceType({
            spaceType: {
              name: cleanName,
              spaceTypeCategory: defaultCategory,
            },
            insertToWorksheet: false,
            clientFacingName: cleanClientFacingName,
            previousId,
            callback: () => {
              if (onCreate) {
                onCreate(spaceTypeCategory);
              }
            },
          })
        );
      }
    } else if (childSpaceType) {
      dispatch(
        calculatorActions.updateChildWorksheetSpaceType(
          {
            id: childSpaceType.id,
            name: cleanName,
            clientFacingName,
          },
          null
        )
      );
    } else if (
      projectSpaceType &&
      (projectSpaceType.isCustom || projectSpaceType.spaceType.isCustom)
    ) {
      dispatch(
        customSpaceTypeActions.updateCustomSpaceType(
          projectSpaceType.spaceType.id,
          { name: cleanName },
          clientFacingName
        )
      );
    } else if (projectSpaceType) {
      dispatch(
        updateProjectSpaceType({
          projectSpaceTypeId: projectSpaceType.id,
          clientFacingName: cleanClientFacingName,
        })
      );
    }

    onDismiss();
    setCustomSpaceTypeName('');
  };

  useEnterShortcut({ onEnterKeyPressed: handleSave, enabled: isOpen });

  const handleCustomSpaceTypeNameChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const { value } = e.target;

    const newValue = textAreaReachedMaxRows(
      e.target as HTMLTextAreaElement,
      customSpaceTypeName
    )
      ? customSpaceTypeName
      : value;

    setCustomSpaceTypeName(newValue);

    if (namesAreTheSame) {
      setClientFacingName(newValue);
    }
  };

  const handleClientFacingNameChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const { value } = e.target;

    setClientFacingName(
      textAreaReachedMaxRows(e.target as HTMLTextAreaElement, clientFacingName)
        ? clientFacingName
        : value
    );
  };

  const handleCheckboxChange = (
    _e: React.ChangeEvent<HTMLInputElement>,
    value: boolean
  ): void => {
    if (!value) {
      setClientFacingName('');
    } else {
      setClientFacingName(customSpaceTypeName);
    }
  };

  useEffect(() => {
    setIsFormDirty(
      clientFacingName !== initialClientFacingName ||
        customSpaceTypeName !== initialCustomSpaceTypeName
    );
  }, [
    customSpaceTypeName,
    initialCustomSpaceTypeName,
    clientFacingName,
    initialClientFacingName,
  ]);

  const title = useSpaceTypeDialogTitle(parentSpaceType);

  const dialogTitle = useMemo(() => {
    if (isEditingSpaceType) {
      return 'Rename Space Type';
    }
    if (projectSpaceType) {
      return 'Create Custom Space Type';
    }
    return `Create ${title}`;
  }, [isEditingSpaceType, projectSpaceType, title]);

  return (
    <Dialog
      disableScrollLock
      open={isOpen}
      PaperComponent={CustomSpaceTypeModalCard}
      onClose={() => onDismiss()}
    >
      <div id="alert-dialog-title" className="title">
        {dialogTitle}
      </div>
      <DialogContent className="content">
        <InputLabel>Internal Space Type Name</InputLabel>
        <div
          style={{
            width: CUSTOM_SPACE_TYPE_NAME_INPUT_WIDTH,
            minWidth: CUSTOM_SPACE_TYPE_NAME_INPUT_WIDTH,
          }}
        >
          <WorksheetInput
            multiline
            variant="outlined"
            data-testid="internal-name-input"
            value={customSpaceTypeName}
            disabled={
              isEditingSpaceType &&
              !isNil(projectSpaceType) &&
              !projectSpaceType.isCustom &&
              !projectSpaceType.spaceType.isCustom
            }
            onChange={handleCustomSpaceTypeNameChange}
          />
        </div>

        <ErrorMessage
          className="error-message"
          style={{
            opacity: internalNameAlreadyInUse ? 1 : 0,
          }}
        >
          Space type name already exists.
        </ErrorMessage>
        <FormControlLabel
          className="checkbox-label"
          label="Internal and Client Names are the same."
          control={
            <SPWBaseCheckbox
              checked={namesAreTheSame}
              onChange={handleCheckboxChange}
              disabled={!customSpaceTypeName}
            />
          }
        />

        <Box mt={1}>
          <InputLabel>Client Space Type Name (Optional)</InputLabel>
          <div
            style={{
              width: CUSTOM_SPACE_TYPE_NAME_INPUT_WIDTH,
              minWidth: CUSTOM_SPACE_TYPE_NAME_INPUT_WIDTH,
            }}
          >
            <WorksheetInput
              multiline
              variant="outlined"
              data-testid="client-name-input"
              value={clientFacingName}
              onChange={handleClientFacingNameChange}
            />
          </div>
        </Box>

        <ErrorMessage
          className="error-message"
          style={{
            opacity: clientNameAlreadyInUse ? 1 : 0,
          }}
        >
          Space type name already exists.
        </ErrorMessage>
        {isEditingSpaceType &&
        projectSpaceType &&
        !projectSpaceType?.isCustom ? (
          <div className="info">
            <img src={InfoBlackSmall} alt="info" />
            <div className="info-text">
              Enter a <strong>Client Name</strong> when your client uses a
              different name than your internal standards. This is recommended
              over creating a <strong>Custom Space Type.</strong>
            </div>
          </div>
        ) : null}
        {!isEditingSpaceType && (
          <div className="info">
            <img src={WarningIconRedSmall} alt="info" />
            <div className="info-text">
              Only create a <strong>Custom Space Type</strong> when it cannot be
              found in the pre-defined list. Another option is to add a{' '}
              <strong>Client Name</strong> to an
              <strong> Internal Name</strong> on the pre-defined list which
              simply changes the label.
            </div>
          </div>
        )}
      </DialogContent>
      <SPWDialogActions>
        <SPWSaveButton88Width
          data-testid="create-space-type-save"
          onClick={handleSave}
          disabled={!saveButtonIsEnabled}
          type="submit"
        />
        <SPWCancelButton88Width
          customColor={colors.black}
          onClick={() => {
            onDismiss();
            setCustomSpaceTypeName('');
          }}
        />
      </SPWDialogActions>
    </Dialog>
  );
};

export default WorksheetSpaceTypeDialog;
